/**
 * Metric formatting and the delta contract.
 *
 * THE RULE THIS FILE ENFORCES
 * ---------------------------
 * A figure is rendered only when the server confirmed it. `null` and `undefined`
 * are not zero — they render as an em dash, and the caller is expected to show a
 * skeleton or a retry instead. The previous code destructured every metric to
 * `= 0`, so a 503 painted an authoritative all-zero report.
 *
 * Use `fmt*` for display and `deltaTone` for colour. Never colour a delta by its
 * sign alone: a rising answer rate is good, a rising missed-call count is bad,
 * and a rising call volume is neither.
 */

export const EMPTY = '-'; // shown for a missing value

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Integer with thousands separators. Returns EMPTY for null/undefined/NaN. */
export const fmtInt = (v) => (isNum(v) ? new Intl.NumberFormat().format(Math.round(v)) : EMPTY);

/** A ratio (0..1) as a whole-number percent. */
export const fmtPercent = (v, { decimals = 0 } = {}) =>
  isNum(v) ? `${(v * 100).toFixed(decimals)}%` : EMPTY;

/** A value already expressed in percentage points. */
export const fmtPercentPoints = (v, { decimals = 0 } = {}) =>
  isNum(v) ? `${v.toFixed(decimals)}%` : EMPTY;

/** Seconds as m:ss, or h:mm:ss past an hour. */
export const fmtDuration = (seconds) => {
  if (!isNum(seconds)) return EMPTY;
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/** Seconds as a compact prose duration: "4m 12s", "1h 04m", "38s". */
export const fmtDurationWords = (seconds) => {
  if (!isNum(seconds)) return EMPTY;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
};

/** Minutes as "41 min", "1h 20m". Used for callback latency. */
export const fmtMinutes = (mins) => {
  if (!isNum(mins)) return EMPTY;
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${String(rem).padStart(2, '0')}m` : `${h}h`;
};

/** Dispatch on a format name. Keeps StatTile free of formatting branches. */
export const formatValue = (value, format, opts = {}) => {
  switch (format) {
    case 'percent': return fmtPercent(value, opts);
    case 'percentPoints': return fmtPercentPoints(value, opts);
    case 'duration': return fmtDuration(value);
    case 'durationWords': return fmtDurationWords(value);
    case 'minutes': return fmtMinutes(value);
    case 'integer':
    default: return fmtInt(value);
  }
};

/* ─── The delta contract ─────────────────────────────────────────────────── */

/**
 * How a metric's movement should be read.
 *   up_good  — higher is better (answer rate, callback rate, quality, closure)
 *   up_bad   — higher is worse  (missed calls, negative rate, time-to-callback,
 *                                backlog age)
 *   neutral  — movement carries no verdict (call volume, handle time). These get
 *              an arrow and NO colour: volume is context, never performance.
 */
export const DIRECTION = { UP_GOOD: 'up_good', UP_BAD: 'up_bad', NEUTRAL: 'neutral' };

/**
 * Minimum base before a delta is trustworthy. Confidently reporting a 100% swing
 * on three calls is the most damaging failure mode a KPI tile has.
 */
export const DELTA_FLOOR = { rate: 20, count: 10 };

/**
 * Resolve a delta into what the UI should actually render.
 *
 * @param {object|null} delta   { value, base, window } from the API. `value` is
 *   in the metric's own units (a ratio for rates, points for percentPoints, an
 *   absolute count otherwise). `base` is the comparison period's value.
 * @param {string} direction    one of DIRECTION.
 * @param {object} opts         { basis: 'rate'|'count', baseDenominator }
 * @returns {{state, tone, arrow, text, title}|null}
 *   state: 'ok' | 'insufficient' | 'flat' | 'none'
 */
export const resolveDelta = (delta, direction = DIRECTION.NEUTRAL, opts = {}) => {
  const { basis = 'count', baseDenominator = null } = opts;

  if (!delta || !isNum(delta.value)) {
    return { state: 'none', tone: 'flat', arrow: '', text: '', title: '' };
  }

  // Suppress the delta when there is not enough history to compare against.
  const floor = DELTA_FLOOR[basis] ?? DELTA_FLOOR.count;
  if (isNum(baseDenominator) && baseDenominator < floor) {
    return {
      state: 'insufficient',
      tone: 'flat',
      arrow: '',
      text: 'not enough history',
      title: `Only ${fmtInt(baseDenominator)} calls in the comparison period - too few to compare.`,
    };
  }

  const v = delta.value;
  const rising = v > 0;
  const flat = v === 0;

  let tone = 'flat';
  if (!flat && direction !== DIRECTION.NEUTRAL) {
    const good = direction === DIRECTION.UP_GOOD ? rising : !rising;
    tone = good ? 'good' : 'bad';
  }

  return {
    state: flat ? 'flat' : 'ok',
    tone,
    arrow: flat ? '' : rising ? '▲' : '▼',
    text: formatDeltaMagnitude(v, delta.format || opts.format),
    title: delta.window ? `vs ${delta.window}` : '',
  };
};

/** The magnitude half of a delta chip: "4 pts", "23%", "12". */
export const formatDeltaMagnitude = (v, format) => {
  if (!isNum(v)) return '';
  const abs = Math.abs(v);
  switch (format) {
    case 'percent':
      // A change in a ratio metric is stated in percentage POINTS, not as a
      // percentage of a percentage — "up 4 pts", never "up 4.8%".
      return `${(abs * 100).toFixed(abs * 100 < 10 ? 1 : 0).replace(/\.0$/, '')} pts`;
    case 'percentPoints':
      return `${abs.toFixed(abs < 10 ? 1 : 0).replace(/\.0$/, '')} pts`;
    case 'duration':
    case 'durationWords':
      return fmtDurationWords(abs);
    case 'minutes':
      return fmtMinutes(abs);
    case 'relative':
      return `${Math.round(abs * 100)}%`;
    default:
      return fmtInt(abs);
  }
};

/** Tailwind classes for a delta tone. */
export const deltaToneClass = (tone) => {
  switch (tone) {
    case 'good': return 'text-good-text bg-good-tint';
    case 'bad': return 'text-bad-text bg-bad-tint';
    default: return 'text-ink-500 bg-surface-2';
  }
};

/* ─── Denominators and coverage ──────────────────────────────────────────── */

/**
 * "412 of 474 inbound calls". A rate is never rendered without this.
 * Returns null when the numbers are not both known, so the caller renders nothing
 * rather than "0 of 0".
 */
export const denominatorText = (numerator, denominator, noun = 'calls') => {
  if (!isNum(numerator) || !isNum(denominator) || denominator <= 0) return null;
  return `${fmtInt(numerator)} of ${fmtInt(denominator)} ${noun}`;
};

/** Safe ratio. Returns null (not 0, not NaN) when the denominator is unusable. */
export const ratio = (numerator, denominator) => {
  if (!isNum(numerator) || !isNum(denominator) || denominator <= 0) return null;
  return numerator / denominator;
};

/** Coverage below this and every derived figure is a caveat, not a fact. */
export const COVERAGE_WARN = 0.6;

export const coverageText = (analysed, total) => {
  const r = ratio(analysed, total);
  if (r === null) return null;
  return `Based on ${fmtInt(analysed)} of ${fmtInt(total)} calls analysed (${fmtPercent(r)})`;
};

/* ─── Status tone helpers ────────────────────────────────────────────────── */

/**
 * Age against an SLA. Amber past half the window, red past it.
 * Returns 'ok' | 'warn' | 'bad'.
 */
export const slaTone = (minutes, slaMinutes) => {
  if (!isNum(minutes) || !isNum(slaMinutes) || slaMinutes <= 0) return 'ok';
  if (minutes > slaMinutes) return 'bad';
  if (minutes > slaMinutes * 0.5) return 'warn';
  return 'ok';
};

export const ageToneClass = (tone) => {
  switch (tone) {
    case 'bad': return 'text-bad-text';
    case 'warn': return 'text-warn-text';
    default: return 'text-ink-500';
  }
};

/**
 * Sentiment tone. NEUTRAL IS GREY, NOT AMBER — a neutral call is not a warning,
 * and amber is reserved for SLA breach, quota, and trial expiry.
 */
export const sentimentTone = (sentiment) => {
  switch (String(sentiment || '').toLowerCase()) {
    case 'positive': return 'good';
    case 'negative': return 'bad';
    case 'neutral': return 'neutral';
    default: return 'unknown';
  }
};

export const SENTIMENT_META = {
  good:    { label: 'Positive',   icon: 'fa-arrow-up',        chip: 'text-good-text bg-good-tint border-good-line', fill: 'rgb(var(--good-fill))' },
  neutral: { label: 'Neutral',    icon: 'fa-minus',           chip: 'text-ink-700 bg-surface-2 border-line',        fill: 'rgb(var(--slate-fill))' },
  bad:     { label: 'Negative',   icon: 'fa-arrow-down',      chip: 'text-bad-text bg-bad-tint border-bad-line',    fill: 'rgb(var(--bad-fill))' },
  unknown: { label: 'Unanalysed', icon: 'fa-circle-question', chip: 'text-ink-500 bg-surface-2 border-line',        fill: 'rgb(var(--slate-light))' },
};

/** Truncate for a dense cell without cutting mid-word when avoidable. */
export const truncate = (text, max = 48) => {
  const s = String(text ?? '');
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  // Only back off to the previous word boundary when the cut actually lands
  // mid-word; if it already fell on one, keep the full width.
  if (/\s/.test(s.charAt(max))) return `${cut.replace(/\s+$/, '')}…`;
  const sp = cut.lastIndexOf(' ');
  return `${sp > max * 0.6 ? cut.slice(0, sp) : cut}…`;
};

/**
 * What to call a contact: their name, else their email, else their number -
 * never "Unnamed contact" while something identifies them. A "name" that is
 * only a number (old caller-ID rows) counts as no name.
 */
export const contactLabel = ({ name, email, phone } = {}) => {
  const n = String(name ?? '').trim();
  if (n && /[^\d\s+()-]/.test(n)) return n;
  const e = String(email ?? '').trim();
  if (e) return e;
  if (String(phone ?? '').trim()) return fmtPhone(phone);
  return n ? fmtPhone(n) : 'Unnamed contact';
};

/** E.164-ish phone display. Leaves anything unrecognised alone. */
export const fmtPhone = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return EMPTY;
  const digits = s.replace(/\D/g, '');
  if (s.startsWith('+') && digits.length > 10) {
    const cc = digits.slice(0, digits.length - 10);
    const rest = digits.slice(-10);
    return `+${cc} ${rest.slice(0, 5)} ${rest.slice(5)}`;
  }
  return s;
};
